import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as xlsx from 'xlsx'; // https://docs.sheetjs.com/docs/getting-started/examples/export

import { useTheme } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import Divider from '@material-ui/core/Divider';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';

import FilterListIcon from '@material-ui/icons/FilterList';
import TuneIcon from '@material-ui/icons/Tune';
import SearchIcon from '@material-ui/icons/Search';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import DeleteIcon from '@material-ui/icons/Delete';
import ClearIcon from '@material-ui/icons/Clear';

import * as math from '_core/utils/math';
import * as api from '_core/utils/api';
import { timeout } from '_core/utils/async';
import { addExportTask } from '_core/components/FloatingWidgets';


const EXPORT_FORMATS = [
    { title: 'Excel 2007+ XML Formats', rows: 1048576, extension: 'xlsx' },
    { title: 'Excel 2007+ Binary Format', rows: 1048576, extension: 'xlsb' },
    { title: 'OpenDocument Spreadsheet', rows: 1048576, extension: 'ods' },
    { title: 'Excel 97-2004', rows: 65536, extension: 'xls' },
    { title: 'Comma-Separated Values', rows: null, extension: 'csv' },
    { title: 'HTML Tables', rows: null, extension: 'html' },
    //{ title: 'Rich Text Format tables', rows: null, extension: 'rtf' },
    { title: 'Plain text file', rows: null, extension: 'txt' }
  ];


export default React.memo((props) => {
  const {
    langInfo,
    l,
    classes,
    visitorInfo,
    
    tableColumns,
    exportColumns,
    period,
    order,
    setOrder,
    filters,
    filterGet,
    filterApply
  } = props;
  
  
  const [filtersAnchorEl, setFiltersAnchorEl] = React.useState(null);
  const [searchAnchorEl, setSearchAnchorEl] = React.useState(null);
  const [exportAnchorEl, setExportAnchorEl] = React.useState(null);
  const [exportFormat, setExportFormat] = React.useState(EXPORT_FORMATS[0]);
  const [exportFormatOpen, setExportFormatOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  
  
  const visitorId = visitorInfo.info.id;
  
  const filterQueryNameSplit = [['query']];
  
  const filtersList = tableColumns.map((column) => {
    let value = filterGet(column.nameSplit);
    
    const columnName = column.nameSplit[0].join('.');
    
    if (math.isNone(value)) {
      if (order !== columnName && order !== '-' + columnName)
        return null;
      
      value = null;
    } else {
      if (Array.isArray(value)) {
        if (!value.length)
          return null;
      } else if (typeof(value) === 'object' && 'from' in value && 'to' in value) {
        const from = !math.isEmpty(value.from) ? Number(value.from) : '';
        const to = !math.isEmpty(value.to) ? Number(value.to) : '';
        
        if (!Number.isFinite(from) && !Number.isFinite(to))
          return;
        
        value = { from, to };
      }
    }
    
    return { ...column, value };
  }).filter(Boolean);
  
  if (filterGet(filterQueryNameSplit))
    filtersList.unshift({ nameSplit: filterQueryNameSplit, icon: 'search', title: '$backoffice.admin.portal.articles.find', value: filterGet(filterQueryNameSplit) });
  
  const renderFilterColumn = (column, index) => {
    const columnName = column.nameSplit[0].join('.');
    
    const value = column.value;
    
    const filterBtnApply = (val) => { filterApply(column.nameSplit, val); setFiltersAnchorEl(null); };
    
    return (
      <React.Fragment key={index}>
        <ListItem>
          <ListItemIcon>{column.icon ? <Icon>{column.icon}</Icon> : null}</ListItemIcon>
          <ListItemText primary={l(column.title) + ':'} />
          <ListItemSecondaryAction>
            <IconButton
              edge="end"
              onClick={(event) => { event.preventDefault(); filterApply(column.nameSplit, null); setFiltersAnchorEl(null); }}
            >
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        
        <ListItem className={classes.filterValues}>
          {order === columnName ? (
            <div className={classes.statusChip}>
              {l('$backoffice.list.sort.asc')}
              
              <a href="" onClick={(event) => { event.preventDefault(); setOrder(null); setFiltersAnchorEl(null); }}><ClearIcon /></a>
            </div>
          ) : order === '-' + columnName ? (
            <div className={classes.statusChip}>
              {l('$backoffice.list.sort.desc')}
              
              <a href="" onClick={(event) => { event.preventDefault(); setOrder(null); setFiltersAnchorEl(null); }}><ClearIcon /></a>
            </div>
          ) : null}
          
          {value !== null ? (
            <column.TagsComponent
              {...props}
              
              filterApply={filterBtnApply}
              column={column}
              value={value}
            />
          ) : null}
        </ListItem>
      </React.Fragment>
    );
  };
  
  const buildExportFile = () => {
    let isCancel = false;
    
    const format = exportFormat;
    const maxRows = format.rows;
    
    addExportTask(
      /* title */
      l('$r._.export.build').replace('%', format.extension.toUpperCase()),
      
      /* process */
      async (progress) => {
        const token = await api.getClientTokenGenerate(visitorId);
        
        const hits = (await api.rest('get', 'client/visitor/stats', { token, ancestor: visitorId })).hits;
        
        const book = xlsx.utils.book_new();
        
        let sheet = null;
        let page = 0;
        let rows = 0;
        
        const rowsQueue = [];
        
        const flushRowsQueue = async (data) => {
          if (!rowsQueue.length)
            return;
          
          xlsx.utils.sheet_add_aoa(sheet, await Promise.all(rowsQueue), { origin: -1 });
          
          rowsQueue.length = 0;
        };
        
        const addRowsQueue = async (data) => {
          rowsQueue.push(addRow(data));
          
          if (rowsQueue.length >= 100)
            await flushRowsQueue();
        };
        
        const addRow = async (data) => {
          if (!sheet || maxRows && rows >= maxRows) {
            sheet = xlsx.utils.sheet_new();
            
            xlsx.utils.book_append_sheet(book, sheet, l('$r.core.export.sheet.title').replace('%', ++page));
            
            xlsx.utils.sheet_add_aoa(sheet, [exportColumns.map((column) => l(column.title))], { origin: -1 });
            
            rows = 0;
          }
          
          const result = await Promise.all(exportColumns.map((column) => column.getValueExport(props, column, data)));
          
          progress((rows * 100) / hits);
          
          rows++;
          
          return result;
        };
        
        
        const parentSet = new Set();
        
        const generateExportData = async (parentId) => {
          if (isCancel || parentSet.has(parentId))
            return;
          
          parentSet.add(parentId);
          
          const param = {
            token,
            parent: parentId !== visitorId ? parentId : undefined,
            period: period.id,
            order,
            offset: 0,
            limit: 1000
          };
          
          let recursive = true;
          
          if (parentId === visitorId) {
            Object.entries(filters).forEach(([name, value]) => {
              const column = tableColumns.find((item) => item.name === name);
              
              if (!column || math.isNone(value))
                return;
              
              param[name] = column.getValueApiParam(column, value);
              
              recursive = false;
            });
          }
          
          while (!isCancel) {
            const list = await api.getClassicVisitor(param);
            
            if (!list.length)
              break;
            
            param.offset += param.limit;
            
            for (let index = 0, length = list.length; index < length; index++) {
              const item = list[index];
              
              await addRowsQueue(item);
              
              if (recursive && item.level && item.children > 0)
                await generateExportData(item.id);
            }
            
            if (list.length < param.limit)
              break;
          }
        };
        
        await generateExportData(visitorId);
        
        await flushRowsQueue();
        
        return book;
      },
      
      /* download */
      async (book) => {
        xlsx.writeFile(book, l('$r._.export.filename').replace('%', l(period.title)) + '.' + format.extension, { compression: true });
      },
      
      /* cancel */
      () => {
        isCancel = true;
      }
    );
    
    setExportAnchorEl(null);
  };
  
  
  return (
    <Box display="flex" flexWrap="wrap" alignItems="flex-end" justifyContent="space-between" sx={{ gap: 8 }}>
      <Paper elevation={0}>
        <Badge color="primary" variant="dot" overlap="circular" invisible={!filtersList.length}>
          <Button
            className={classes.filterAllBtn}
            variant="text"
            startIcon={<FilterListIcon />}
            disabled={!filtersList.length}
            onClick={(event) => setFiltersAnchorEl(event.currentTarget)}
          >
            {l('$backoffice.admin.reports.build.filters')}
          </Button>
        </Badge>
      </Paper>
      
      <Paper elevation={0}>
        <Box display="flex" flexWrap="wrap" sx={{ gap: 8 }}>
          {/*<Button
            className={classes.filterAllBtn}
            variant="text"
            startIcon={<TuneIcon />}
          >
            {l('$backoffice.admin.service.config')}
          </Button>*/}
          
          <Badge color="primary" variant="dot" overlap="circular" invisible={!filterGet(filterQueryNameSplit)}>
            <Button
              className={classes.filterAllBtn}
              variant="text"
              startIcon={<SearchIcon />}
              onClick={(event) => setSearchAnchorEl(event.currentTarget)}
            >
              {l('$backoffice.admin.portal.articles.find')}
            </Button>
          </Badge>
          
          {exportColumns.length ? (
            <Button
              className={classes.filterExportBtn}
              variant="contained"
              onClick={(event) => setExportAnchorEl(event.currentTarget)}
            >
              {l('$global.buttons.export')}
            </Button>
          ) : null}
        </Box>
        
        
        {/* filters menu */}
        
        <Menu
          variant="menu"
          elevation={0}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorEl={filtersAnchorEl}
          keepMounted
          open={Boolean(filtersAnchorEl)}
          onClose={() => setFiltersAnchorEl(null)}
        >
          <ListItem className={classes.filterHeader}>
            <ListItemIcon><FilterListIcon /></ListItemIcon>
            <ListItemText primary={l('$backoffice.admin.reports.build.filters')} />
          </ListItem>
          
          <Divider />
          
          {filtersList.length > 0 ? (
            <>
              {filtersList.map(renderFilterColumn)}
              
              <Divider />
              
              <ListItem className={classes.filterApply}>
                <div style={{ width: '100%' }}></div>
                <Button size="small" color="secondary" fullWidth onClick={() => { setOrder(null); filtersList.forEach((column) => filterApply(column.nameSplit, null)); setFiltersAnchorEl(null); }}>{l('$global.buttons.reset')}</Button>
              </ListItem>
            </>
          ) : (
            <ListItem>
              {l('global.field.none')}
            </ListItem>
          )}
        </Menu>
        
        
        {/* search menu */}
        
        <Menu
          variant="menu"
          elevation={0}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorEl={searchAnchorEl}
          keepMounted
          open={Boolean(searchAnchorEl)}
          onClose={() => setSearchAnchorEl(null)}
        >
          <ListItem className={classes.filterHeader}>
            <ListItemIcon><SearchIcon /></ListItemIcon>
            <ListItemText primary={l('$backoffice.admin.portal.articles.find')} />
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <OutlinedInput
              className={classes.filterInput}
              fullWidth
              margin="none"
              placeholder={l('$backoffice.admin.portal.articles.find')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              startAdornment={(
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )}
            />
          </ListItem>
          
          <Divider />
          
          <ListItem className={classes.filterApply}>
            <Button size="small" fullWidth onClick={() => { filterApply(filterQueryNameSplit, searchQuery); setSearchAnchorEl(null); }}>{l('$global.buttons.apply')}</Button>
            <Button size="small" color="secondary" fullWidth onClick={() => { filterApply(filterQueryNameSplit, null); setSearchQuery(''); setSearchAnchorEl(null); }}>{l('$global.buttons.reset')}</Button>
          </ListItem>
        </Menu>
        
        
        {/* export menu */}
        
        <Menu
          variant="menu"
          elevation={0}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          anchorEl={exportAnchorEl}
          keepMounted
          open={Boolean(exportAnchorEl)}
          onClose={() => setExportAnchorEl(null)}
        >
          <ListItem className={classes.filterHeader}>
            <ListItemIcon><SaveAltIcon /></ListItemIcon>
            <ListItemText primary={l('$global.buttons.export')} />
          </ListItem>
          
          <Divider />
          
          <MenuItem className={classes.exportPropTitle}>
            <ListItemText
              primary={l('$global.field.format')}
              secondary={exportFormat.extension.toUpperCase() + ' (' + l(exportFormat.title) + ')'}
              onClick={() => setExportFormatOpen(!exportFormatOpen)}
            />
          </MenuItem>
          
          <ListItem className={clsx(classes.exportFormatList, exportFormatOpen && classes.exportFormatOpen)} disableGutters>
            <MenuList disablePadding>
              {EXPORT_FORMATS.map((item) => (
                <MenuItem key={item.extension} onClick={() => { setExportFormat(item); setExportFormatOpen(false); }}>
                  <ListItemText primary={item.extension.toUpperCase() + ' (' + l(item.title) + ')'} />
                  <ListItemSecondaryAction>
                    <Radio
                      edge="end"
                      onChange={() => { setExportFormat(item); setExportFormatOpen(false); }}
                      checked={exportFormat === item}
                    />
                  </ListItemSecondaryAction>
                </MenuItem>
              ))}
            </MenuList>
          </ListItem>
          
          <Divider />
          
          <ListItem className={classes.filterApply}>
            <div style={{ width: '100%' }}></div>
            <Button size="small" fullWidth onClick={buildExportFile}>{l('$global.buttons.export')}</Button>
          </ListItem>
        </Menu>
      </Paper>
    </Box>
  );
});
